import { isGuitarChord } from '../guitar/guitar-chord.js'
import { GuitarLib } from '../guitar/index.js'
import type { NoteOctave } from '../note/index.js'
import * as NoteLib from '../note/note-lib.js'
import { ScaleLib } from '../scale/index.js'
import { isChordNotes, isChordVariant } from './chord-types.js'
import type { Chord, ChordNotes } from './chord-types.js'
import { noteSignature } from './util.js'

/** Chord interface */

const symbolPattern = /^([A-G][#b]?)(.*)$/
const bassPattern = /^(.*)\/([A-G][#b]?)$/

/** A chord symbol's root and slash bass moved; a name that is not a chord symbol (Custom, N.C.) is kept */
const transposeName = (name: string, semiTones: number) => {
  const symbol = symbolPattern.exec(name)
  if (!symbol || !fromName(name)) {
    return name
  }
  const [, root, rest] = symbol
  const slash = bassPattern.exec(rest)
  const movedRoot = NoteLib.transpose(root as NoteName, semiTones)
  return slash ? `${movedRoot}${slash[1]}/${NoteLib.transpose(slash[2] as NoteName, semiTones)}` : `${movedRoot}${rest}`
}

export const transpose = <T extends Chord>(chord: T, semiTones: number): T => {
  if (semiTones === 0) {
    return chord
  }
  if (isGuitarChord(chord)) {
    return {
      ...chord,
      name: getName(chord, semiTones),
      notes: chord.notes.map(n => NoteLib.transpose(n, semiTones)),
      fingering: GuitarLib.transpose({ fingerings: chord.fingering, steps: semiTones }),
    }
  }
  if (isChordNotes(chord)) {
    return {
      ...chord,
      /** The name moves with the notes, or the chip reads G/B over A C♯ E (GH #2982) */
      name: transposeName(getName(chord), semiTones),
      notes: chord.notes.map(n => NoteLib.transpose(n, semiTones)),
    }
  }
  if (isChordVariant(chord)) {
    /** The slash bass moves with the root, or G/B up 2 reads A/B (GH #2982) */
    return chord.bassNote
      ? {
          ...chord,
          root: NoteLib.transpose(chord.root, semiTones),
          bassNote: NoteLib.transpose(chord.bassNote, semiTones),
        }
      : { ...chord, root: NoteLib.transpose(chord.root, semiTones) }
  }
  logger.error(`Unknown chord shape - cannot transpose`, chord)
  return chord
}

/**
 * A chord moved `semiTones` on a chart written in `scale`: by the interval between the written key and the key it
 * sounds in, so a borrowed or applied chord keeps its spelling (Ab in C up 7 is Eb, not D#; GH #2982). Without a key,
 * and for a fingered guitar shape, it is `transpose`.
 */
export const transposeInKey = <T extends Chord>(chord: T, semiTones: number, scale?: Scale | null): T => {
  /** A scale with no root (a malformed or legacy key) is no key */
  if (!scale?.root || isGuitarChord(chord)) {
    return transpose(chord, semiTones)
  }
  return transposeChordInKey(chord, semiTones, concertKeyOf(scale, semiTones, 'shapes'), scale)
}

/**
 * A note moved `semiTones` from the chart's key: a chord tone, such as the voice-leading connector's added and dropped
 * notes. With the key known it moves by the interval and T3, never T4 (Bowie's notes-list rule, GH #2990), so G in C up
 * 8 is E♭, not D♯. Given the chord it belongs to, it follows that chord as its chip spells it: when T4 respells the
 * chord's root, the note takes the same letter step (G of E♭, in C up 8, is D♯ of B). Without a key it moves as
 * `NoteLib.transpose` does.
 */
export const transposeToneInKey = (
  note: NoteName,
  semiTones: number,
  scale?: Scale | null,
  chordName?: string
): NoteName => {
  if (chordName) {
    const owner: ChordNotes = { name: chordName, notes: [note] }
    return transposeInKey(owner, semiTones, scale).notes[0] ?? note
  }
  const toKey = scale?.root ? concertKeyOf(scale, semiTones, 'shapes') : undefined
  const interval = scale?.root && toKey ? keyInterval(scale, toKey) : undefined
  const moved =
    interval && interval.semitones === ((semiTones % 12) + 12) % 12 ? moveToneByInterval(note, interval) : undefined
  return moved ?? NoteLib.transpose(note, semiTones)
}

import { ALL_CHORDS, notesToChords } from './all-chords.js'
import { concertKeyOf, keyInterval, moveToneByInterval, transposeChordInKey } from './concert-pitch.js'
import { getName, transposeLabel } from './get-name.js'
export { getName, transposeLabel }
export { spokenChordName } from './spoken-name.js'
export { chordForTypedName, filterChordsByName, normalizeChordQuery } from './filter-by-name.js'

export { getNotes } from './get-notes.js'

import { logger } from '@tunasong/models'
import type { NoteName, Scale } from '@tunasong/schemas'
import { chordEquals } from './equals.js'
import { fromName } from './from-name.js'
export { chordEquals as equals }
export { fromName }

/** Custom chords are filtered on their specified name. We should perhaps filter on the notes? */
export const dedupe = (chords: Chord[]) => {
  const chordMap: Record<string, Chord> = {}
  chords.forEach(c => (chordMap[getName(c)] = c))
  return Object.values(chordMap)
}

export const getChordNames = () => Object.keys(ALL_CHORDS)

interface FromNotesOptions {
  /** If scale is specified, only chords whose root are in the scale are returned */
  scale?: Scale
  /** include bb, ## and E# and B#. @default to false */
  includeUncommon?: boolean
}

const isUncommon = (note: NoteName) => {
  const uncommon = ['E#', 'B#']
  return uncommon.includes(note) || note.endsWith('bb') || note.endsWith('##')
}

export const getBassNote = (notes: (NoteOctave | NoteName)[]): NoteName | null => {
  const notesWithOctave = notes
    .filter(note => NoteLib.isNoteOctave(note))
    .sort((a, b) => NoteLib.frequencyFromNoteName(a) - NoteLib.frequencyFromNoteName(b))
  const note = notesWithOctave[0]
  return note ? NoteLib.getNoteAndOctave(note)?.note : null
}

export const fromNotes = (
  notes: (NoteName | NoteOctave)[],
  { scale, includeUncommon }: FromNotesOptions = { includeUncommon: false }
) => {
  const sign = noteSignature(notes.map(NoteLib.toNote))
  const chords = (notesToChords[sign] || []).filter(c => (includeUncommon ? true : !isUncommon(c.root)))
  const scaleNotes = scale ? ScaleLib.getNotes(scale) : null

  const chordCandidates = scaleNotes ? chords.filter(c => scaleNotes.includes(c.root)) : chords

  // Find the bass note if the notes have octave information
  const bassNote = getBassNote(notes)
  const mappedChords = bassNote
    ? chordCandidates.map(c => ({
        ...c,
        bassNote: bassNote !== c.root ? bassNote : undefined,
      }))
    : chordCandidates

  return mappedChords
}

export const unique = (chords: Chord[]) => {
  const names = [...new Set(chords.map(c => getName(c)))]
  return names.map(name => chords.find(c => getName(c) === name) as Chord).filter(Boolean)
}
